import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ExternalLink, Search, ChevronDown, Info, Building2 } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Tooltip } from '@/components/ui/Tooltip'
import { usePresalesDashboardStore } from '../store'
import { usePresalesDashboardStore as useCalendarStore } from '@/store'
import { formatCurrency, formatDate, getFirstName, isHandedOver } from '../utils/dealHelpers'
import { ProgressDonut } from '@/features/scope/components/ProgressDonut'
import { computeStepStatuses, buildActionItemsFromBooleans } from '@/features/scope/utils/progressSteps'
import type { PresalesDeal } from '@/api/types'

// Build the scopeStatusDates + action items a deal needs to drive the progress donut.
function dealToStepSources(deal: PresalesDeal) {
  const actions = buildActionItemsFromBooleans({
    sandbox: deal.action_sandbox,
    uat: deal.action_uat,
    help_doc_sent: deal.action_help_doc_sent,
    scope_confirmed: deal.action_scope_confirmed,
    pipeline_products_added: deal.action_pipeline_products_added,
    pipeline_products_discussed: deal.action_pipeline_products_discussed,
    pipeline_products_confirmed_standard: deal.action_pipeline_products_confirmed_standard,
    implementations_reviewing_sow: deal.action_implementations_reviewing_sow,
  })
  const scopeStatusDates: Record<string, string | null> = {
    draft: deal.scope_started_date ?? null,
    sent: deal.scope_sent_date ?? null,
    confirm: deal.scope_confirmed_date ?? null,
    handover: deal.handover_date ?? null,
  }
  return { actions, scopeStatusDates }
}
import { usePresalesMembers } from '../utils/taskHelpers'
import { ReassignProspectDialog } from './ReassignProspectDialog'
import { presalesDashboardApi } from '@/api/client'

type StatusOption = 'open' | 'closed_won' | 'closed_lost'

export function PresalesDealsTable({ dealsTab = 'current' }: { dealsTab?: 'current' | 'handover' }) {
  const navigate = useNavigate()
  const { deals, isLoadingDeals } = usePresalesDashboardStore()
  const { eventLinkSummary, fetchEventLinkSummary } = useCalendarStore()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedStatuses, setSelectedStatuses] = useState<StatusOption[]>(['open', 'closed_won']) // Closed Lost deselected by default
  const [selectedOwners, setSelectedOwners] = useState<string[]>([]) // Empty = all owners (populated on mount)
  const [sortBy, setSortBy] = useState<'name' | 'amount' | 'days_open' | 'engagement_date' | 'scope_completion' | 'blueprint_completion' | 'hours' | 'stage' | 'handover_date' | 'probability' | 'actions_progress' | 'close_signal'>('scope_completion')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Default sort for the handover tab: most-recent handover first
  useEffect(() => {
    if (dealsTab === 'handover') {
      setSortBy('handover_date')
      setSortDirection('desc')
    } else {
      setSortBy('scope_completion')
      setSortDirection('desc')
    }
  }, [dealsTab])
  const [assignedOnly, setAssignedOnly] = useState(true)
  const [hidePartnerDeals, setHidePartnerDeals] = useState(true)
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false)
  const [ownerDropdownOpen, setOwnerDropdownOpen] = useState(false)
  const tableRef = useRef<HTMLTableElement>(null)

  useEffect(() => {
    fetchEventLinkSummary()
  }, [fetchEventLinkSummary])

  // Presales team members for owner filter (loaded from DB)
  const presalesMembers = usePresalesMembers()
  const uniqueOwners = presalesMembers

  // Initialize selectedOwners when deals load (select all by default)
  useEffect(() => {
    if (uniqueOwners.length > 0 && selectedOwners.length === 0) {
      setSelectedOwners(uniqueOwners)
    }
  }, [uniqueOwners])

  const getEventLinkData = (dealId: number) => {
    if (!eventLinkSummary) return null
    return eventLinkSummary.by_deal.find(d => d.deal_id === dealId) || null
  }

  // Sync scroll between table and sticky scrollbar
  const handleTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollSyncDiv = document.getElementById('scroll-sync')
    if (scrollSyncDiv) {
      scrollSyncDiv.scrollLeft = e.currentTarget.scrollLeft
    }
  }

  const handleScrollSyncScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const tableContainer = document.getElementById('table-container')
    if (tableContainer) {
      tableContainer.scrollLeft = e.currentTarget.scrollLeft
    }
  }

  const toggleStatus = (status: StatusOption) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status)
      } else {
        return [...prev, status]
      }
    })
  }

  const toggleOwner = (owner: string) => {
    setSelectedOwners(prev => {
      if (prev.includes(owner)) {
        return prev.filter(o => o !== owner)
      } else {
        return [...prev, owner]
      }
    })
  }

  const getStatusLabel = () => {
    if (selectedStatuses.length === 3) return 'All Status'
    if (selectedStatuses.length === 0) return 'No Status Selected'
    const labels = {
      open: 'Open',
      closed_won: 'Won',
      closed_lost: 'Lost'
    }
    return selectedStatuses.map(s => labels[s]).join(', ')
  }

  const getOwnerLabel = () => {
    if (selectedOwners.length === uniqueOwners.length) return 'All Owners'
    if (selectedOwners.length === 0) return 'No Owner Selected'
    return selectedOwners.map(o => o.split(' ')[0]).join(', ')
  }

  // Filter and sort deals
  const filteredDeals = useMemo(() => {
    if (!deals) return []

    let filtered = [...deals]

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(deal =>
        deal.deal_name.toLowerCase().includes(query) ||
        deal.scope_customer_name?.toLowerCase().includes(query) ||
        deal.dealstage?.toLowerCase().includes(query)
      )
    }

    // Apply status filter (multi-select)
    if (selectedStatuses.length > 0 && selectedStatuses.length < 3) {
      filtered = filtered.filter(deal => {
        const markedLost = !!(deal as any).dealhub_marked_lost
        if (selectedStatuses.includes('closed_lost') && (markedLost || (deal.is_closed && !deal.closed_won))) return true
        if (markedLost) return false // Don't show marked-lost deals in other filters
        if (selectedStatuses.includes('open') && !deal.is_closed) return true
        if (selectedStatuses.includes('closed_won') && deal.is_closed && deal.closed_won) return true
        return false
      })
    }

    // Apply owner filter (multi-select)
    if (selectedOwners.length > 0 && selectedOwners.length < uniqueOwners.length) {
      filtered = filtered.filter(deal =>
        selectedOwners.includes(deal.presales_engineer?.trim() || '') ||
        selectedOwners.includes(deal.solutions_consultant_owner?.trim() || '')
      )
    }

    // Exclude live deals
    filtered = filtered.filter(deal =>
      !(deal.dealstage || deal.deal_stage || '').toLowerCase().includes('live')
    )

    // Exclude deals that skipped presales (no engineer/consultant and no engagement date)
    // Skip this filter when user is actively searching — let them find any deal
    if (!searchQuery) {
      filtered = filtered.filter(deal =>
        (deal.presales_engineer && deal.presales_engineer.trim() !== '') ||
        (deal.solutions_consultant_owner && deal.solutions_consultant_owner.trim() !== '') ||
        (deal.presales_engagement_date && deal.presales_engagement_date.trim() !== '') ||
        !!deal.close_signal_strength
      )
    }

    // Hide partner deals
    if (hidePartnerDeals) {
      filtered = filtered.filter(deal =>
        !(deal.pipeline || '').toLowerCase().includes('partner')
      )
    }

    // Filter to only deals with a presales consultant assigned
    if (assignedOnly) {
      filtered = filtered.filter(deal =>
        (deal.presales_engineer && deal.presales_engineer.trim() !== '') ||
        (deal.solutions_consultant_owner && deal.solutions_consultant_owner.trim() !== '')
      )
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0

      switch (sortBy) {
        case 'name':
          comparison = (a.scope_customer_name || a.deal_name).localeCompare(b.scope_customer_name || b.deal_name)
          break
        case 'amount':
          comparison = (a.amount || 0) - (b.amount || 0)
          break
        case 'days_open':
          comparison = (a.days_open || 0) - (b.days_open || 0)
          break
        case 'engagement_date':
          const dateA = a.presales_engagement_date || ''
          const dateB = b.presales_engagement_date || ''
          comparison = dateA.localeCompare(dateB)
          break
        case 'handover_date': {
          // Missing dates always sort to the end regardless of direction.
          // Pre-invert so the outer direction flip leaves them pinned last.
          const aHas = !!a.handover_date
          const bHas = !!b.handover_date
          if (aHas && !bHas) comparison = sortDirection === 'desc' ? 1 : -1
          else if (!aHas && bHas) comparison = sortDirection === 'desc' ? -1 : 1
          else comparison = (a.handover_date || '').localeCompare(b.handover_date || '')
          break
        }
        case 'scope_completion':
          comparison = Number(a.scope_completion_percent || 0) - Number(b.scope_completion_percent || 0)
          break
        case 'blueprint_completion':
          comparison = Number(a.blueprint_completion_percent || 0) - Number(b.blueprint_completion_percent || 0)
          break
        case 'hours': {
          const aLink = getEventLinkData(a.id)
          const bLink = getEventLinkData(b.id)
          const aHrs = (aLink?.presales_minutes || 0) || (a.total_presales_hours || 0) * 60
          const bHrs = (bLink?.presales_minutes || 0) || (b.total_presales_hours || 0) * 60
          comparison = aHrs - bHrs
          break
        }
        case 'stage': {
          // Closed Won always first, Closed Lost always second, then open deals by stage number.
          // Within the same stage, break ties by probability (higher likelihood first) so the
          // "about to close" deals float to the top.
          const stageRank = (deal: typeof a) => {
            if (deal.is_closed && deal.closed_won && !(deal as any).dealhub_marked_lost) return 0
            if ((deal.is_closed && !deal.closed_won) || (deal as any).dealhub_marked_lost) return 1
            return 2
          }
          const stageNumber = (stage: string) => {
            const match = stage.match(/(\d+)/)
            return match ? parseInt(match[1], 10) : 0
          }
          const rankA = stageRank(a)
          const rankB = stageRank(b)
          if (rankA !== rankB) {
            // Pin rank order: apply direction correction so the outer flip doesn't affect it
            const rankComparison = rankA - rankB
            comparison = sortDirection === 'desc' ? -rankComparison : rankComparison
          } else {
            const stageCmp = stageNumber(a.dealstage || '') - stageNumber(b.dealstage || '')
            if (stageCmp !== 0) {
              comparison = stageCmp
            } else {
              // Same stage — higher probability should come first within that stage regardless
              // of the outer direction flip, so pre-invert it.
              const probCmp = (b.deal_stage_probability ?? -1) - (a.deal_stage_probability ?? -1)
              comparison = sortDirection === 'desc' ? -probCmp : probCmp
            }
          }
          break
        }
        case 'probability': {
          // Primary: close_date (closest to today first). Missing dates sort to the end.
          const now = Date.now()
          const aDate = a.close_date ? Math.abs(new Date(a.close_date).getTime() - now) : null
          const bDate = b.close_date ? Math.abs(new Date(b.close_date).getTime() - now) : null
          const aHasDate = aDate != null
          const bHasDate = bDate != null
          if (aHasDate && !bHasDate) comparison = sortDirection === 'desc' ? 1 : -1
          else if (!aHasDate && bHasDate) comparison = sortDirection === 'desc' ? -1 : 1
          else if (aHasDate && bHasDate && aDate !== bDate) {
            // Smaller distance = closer to today = should appear first
            comparison = aDate - bDate
          } else {
            // Tiebreaker: close probability
            comparison = (a.deal_stage_probability ?? 0) - (b.deal_stage_probability ?? 0)
          }
          break
        }
        case 'actions_progress': {
          // Sort by how many donut slices are done (X / 11).
          const doneCount = (deal: typeof a) => {
            const { actions, scopeStatusDates } = dealToStepSources(deal)
            return computeStepStatuses(actions, scopeStatusDates, deal.blueprint_completion_percent ?? 0).filter((s) => s.state === 'done').length
          }
          comparison = doneCount(a) - doneCount(b)
          break
        }
        case 'close_signal': {
          const signalOrder: Record<string, number> = { hot: 0, warm: 1, cool: 2, cold: 3 }
          const aVal = a.close_signal_strength ? signalOrder[a.close_signal_strength] ?? 4 : 5
          const bVal = b.close_signal_strength ? signalOrder[b.close_signal_strength] ?? 4 : 5
          comparison = aVal - bVal
          break
        }
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return filtered
  }, [deals, searchQuery, selectedStatuses, selectedOwners, assignedOnly, hidePartnerDeals, sortBy, sortDirection, eventLinkSummary])

  // Split filtered deals into current vs handover.
  // Uses scope lifecycle status as source of truth (see isHandedOver) so a
  // deal reverted from 'handover' back to 'confirm' returns to Pre-handover.
  const { currentDeals, handoverDeals } = useMemo(() => ({
    currentDeals: filteredDeals.filter(d => !isHandedOver(d)),
    handoverDeals: filteredDeals.filter(d => isHandedOver(d)),
  }), [filteredDeals])

  // When searching, show results from both tabs so users can find any deal
  const activeDeals = searchQuery
    ? filteredDeals
    : dealsTab === 'current' ? currentDeals : handoverDeals

  // Set the scroll sync spacer width to match table width
  useEffect(() => {
    const updateScrollSyncWidth = () => {
      const table = tableRef.current
      const scrollSyncSpacer = document.getElementById('scroll-sync-spacer')
      if (table && scrollSyncSpacer) {
        scrollSyncSpacer.style.width = `${table.scrollWidth}px`
      }
    }

    updateScrollSyncWidth()
    // Use a small delay to ensure table has rendered
    setTimeout(updateScrollSyncWidth, 100)
    window.addEventListener('resize', updateScrollSyncWidth)
    return () => window.removeEventListener('resize', updateScrollSyncWidth)
  }, [deals, activeDeals])

  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortDirection('desc')
    }
  }

  const handleToggleAction = useCallback(async (dealId: number, actionId: string, currentValue: boolean) => {
    const key = `action_${actionId}`
    const newValue = !currentValue

    // Mirror the server-side mutex (sandbox ↔ uat): when turning one ON, the other gets unset.
    // Keeping the optimistic state in sync prevents the donut from showing a stale letter.
    const MUTEX_PAIRS: Record<string, string[]> = {
      sandbox: ['uat'],
      uat: ['sandbox'],
    }
    const mutexPeers = newValue ? (MUTEX_PAIRS[actionId] || []) : []
    const previousPeerValues: Record<string, boolean> = {}

    usePresalesDashboardStore.setState((state) => ({
      deals: state.deals.map((d) => {
        if (d.id !== dealId) return d
        const updated: any = { ...d, [key]: newValue }
        for (const peerId of mutexPeers) {
          const peerKey = `action_${peerId}` as const
          previousPeerValues[peerKey] = !!(d as any)[peerKey]
          updated[peerKey] = false
        }
        return updated
      }),
    }))

    const res = await presalesDashboardApi.toggleDealAction(dealId, actionId, newValue)
    if (!res.success) {
      // Revert everything we changed.
      usePresalesDashboardStore.setState((state) => ({
        deals: state.deals.map((d) => {
          if (d.id !== dealId) return d
          const reverted: any = { ...d, [key]: currentValue }
          for (const [peerKey, prev] of Object.entries(previousPeerValues)) {
            reverted[peerKey] = prev
          }
          return reverted
        }),
      }))
    }
  }, [])

  const getStatusBadge = (deal: any) => {
    if (deal.dealhub_marked_lost) {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-900/30 text-red-300 border border-red-700/50" title="Marked lost in DealHub">Lost (DH)</span>
    }
    if (!deal.is_closed) {
      return <span className="px-2 py-1 text-xs rounded-full bg-blue-900/30 text-blue-300 border border-blue-700/50">Open</span>
    }
    if (deal.closed_won) {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-900/30 text-green-300 border border-green-700/50">Won</span>
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-red-900/30 text-red-300 border border-red-700/50">Lost</span>
  }

  const SortIcon = ({ column }: { column: typeof sortBy }) => {
    if (sortBy !== column) return <span className="text-[var(--text-muted)] ml-1">↕</span>
    return sortDirection === 'asc' ? <span className="ml-1">↑</span> : <span className="ml-1">↓</span>
  }

  const ColumnHeader = ({
    label,
    tooltip,
    sortable = false,
    sortColumn,
    width,
    sticky = false
  }: {
    label: string
    tooltip?: React.ReactNode
    sortable?: boolean
    sortColumn?: typeof sortBy
    width?: string
    sticky?: boolean
  }) => (
    <th
      className={`text-left px-4 py-3 text-xs font-semibold text-[var(--text-secondary)] ${sortable ? 'cursor-pointer hover:text-[var(--text-primary)]' : ''} ${sticky ? 'sticky left-0 z-20 bg-[var(--bg-tertiary)] shadow-[2px_0_4px_rgba(0,0,0,0.1)]' : ''}`}
      onClick={sortable && sortColumn ? () => handleSort(sortColumn) : undefined}
      style={width ? { width, minWidth: width, maxWidth: width } : undefined}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {sortable && sortColumn && <SortIcon column={sortColumn} />}
        {tooltip && (
          <Tooltip content={tooltip}>
            <Info className="h-3 w-3 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-help" />
          </Tooltip>
        )}
      </div>
    </th>
  )

  if (isLoadingDeals) {
    return (
      <Card className="p-6">
        <div className="text-center text-[var(--text-muted)]">Loading deals...</div>
      </Card>
    )
  }

  return (
    <Card className="overflow-hidden">
      {/* Filters Header */}
      <div className="p-4 border-b border-[var(--border-color)] space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-muted)]" />
            <input
              type="text"
              placeholder="Search deals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg text-sm bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)] focus:outline-none focus:ring-2 focus:ring-[var(--button-primary-bg)]"
            />
          </div>

          {/* Status Filter - Multi-select */}
          <div className="relative">
            <button
              onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] transition-colors min-w-[140px]"
            >
              <span className="flex-1 text-left">{getStatusLabel()}</span>
              <ChevronDown className="h-4 w-4" />
            </button>

            {statusDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setStatusDropdownOpen(false)}
                />
                <div className="absolute top-full left-0 mt-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg z-30 min-w-[140px]">
                  <label className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--bg-tertiary)] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes('open')}
                      onChange={() => toggleStatus('open')}
                      className="rounded"
                    />
                    <span className="text-sm">Open</span>
                  </label>
                  <label className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--bg-tertiary)] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes('closed_won')}
                      onChange={() => toggleStatus('closed_won')}
                      className="rounded"
                    />
                    <span className="text-sm">Closed Won</span>
                  </label>
                  <label className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--bg-tertiary)] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedStatuses.includes('closed_lost')}
                      onChange={() => toggleStatus('closed_lost')}
                      className="rounded"
                    />
                    <span className="text-sm">Closed Lost</span>
                  </label>
                </div>
              </>
            )}
          </div>

          {/* Owner Filter - Multi-select */}
          <div className="relative">
            <button
              onClick={() => setOwnerDropdownOpen(!ownerDropdownOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-[var(--bg-secondary)] text-[var(--text-primary)] border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] transition-colors min-w-[140px]"
            >
              <span className="flex-1 text-left">{getOwnerLabel()}</span>
              <ChevronDown className="h-4 w-4" />
            </button>

            {ownerDropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setOwnerDropdownOpen(false)}
                />
                <div className="absolute top-full left-0 mt-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg z-30 min-w-[160px]">
                  {uniqueOwners.map((owner) => (
                    <label key={owner} className="flex items-center gap-2 px-3 py-2 hover:bg-[var(--bg-tertiary)] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedOwners.includes(owner)}
                        onChange={() => toggleOwner(owner)}
                        className="rounded"
                      />
                      <span className="text-sm">{owner}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
          {/* Assigned Only Toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={assignedOnly}
              onChange={() => setAssignedOnly(!assignedOnly)}
              className="rounded"
            />
            <span className="text-sm text-[var(--text-secondary)] whitespace-nowrap">Assigned only</span>
          </label>
          {/* Hide Partner Deals Toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hidePartnerDeals}
              onChange={() => setHidePartnerDeals(!hidePartnerDeals)}
              className="rounded"
            />
            <span className="text-sm text-[var(--text-secondary)] whitespace-nowrap">Hide partner deals</span>
          </label>
        </div>

        {/* Results count */}
        <div className="text-sm text-[var(--text-muted)]">
          Showing {activeDeals.length} of {deals?.length || 0} deals
        </div>
      </div>

      {/* Sticky horizontal scrollbar */}
      <div
        id="scroll-sync"
        className="sticky bottom-0 overflow-x-auto overflow-y-hidden bg-[var(--bg-primary)] border-t border-[var(--border-color)] z-20"
        onScroll={handleScrollSyncScroll}
        style={{ height: '20px', marginTop: '-1px' }}
      >
        <div id="scroll-sync-spacer" style={{ height: '1px', minWidth: '100%' }}>
          {/* Spacer to create scrollbar - width synced with table */}
        </div>
      </div>

      {/* Table */}
      <div
        id="table-container"
        className="overflow-x-auto overflow-y-visible"
        style={{ overflowX: 'scroll' }}
        onScroll={handleTableScroll}
      >
        <table ref={tableRef} className="w-full min-w-max">
          <thead className="bg-[var(--bg-tertiary)] border-b border-[var(--border-color)]">
            <tr>
              {/* 1. Deal Name */}
              <ColumnHeader label="Deal Name" sortable sortColumn="name" width="200px" sticky />
              {/* 2. Deal Actions */}
              <th
                className="text-left px-3 py-3 text-xs font-semibold text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)]"
                onClick={() => handleSort('actions_progress')}
                style={{ width: '130px', minWidth: '130px', maxWidth: '130px' }}
              >
                <div className="flex items-center gap-1">
                  <span>Deal Actions</span>
                  <SortIcon column="actions_progress" />
                  <Tooltip content={
                    <>
                      <strong>Presales progress donut:</strong><br/>
                      Each slice is one step of the presales flow. Hover a slice for its label and date.<br/>
                      Click an action-backed slice to toggle it (Sandbox/UAT cycles through its alternatives).<br/>
                      Sort: orders rows by how many slices are done (e.g. 7/10 before 3/10).
                    </>
                  }>
                    <Info className="h-3 w-3 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-help" />
                  </Tooltip>
                </div>
              </th>
              {/* 3. Hours */}
              <th
                className="text-left px-2 py-3 text-xs font-semibold text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)]"
                onClick={() => handleSort('hours')}
                style={{ width: '95px', minWidth: '95px', maxWidth: '95px' }}
              >
                <div className="flex items-center gap-1">
                  <span>Hours</span>
                  <SortIcon column="hours" />
                  <Tooltip content={
                    <>
                      <strong>Tracked from Calendar Events:</strong><br/>
                      • Presales hours (team time)<br/>
                      • FrankieOne hours (total meeting time)<br/><br/>
                      Format: Presales hrs / F1 hrs<br/>
                      Sorted by presales hours.
                    </>
                  }>
                    <Info className="h-3 w-3 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-help" />
                  </Tooltip>
                </div>
              </th>
              {/* 3. Scope % */}
              <th
                className="text-center px-4 py-3 text-xs font-semibold text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)]"
                onClick={() => handleSort('scope_completion')}
                style={{ width: '110px', minWidth: '110px', maxWidth: '110px' }}
              >
                <div className="flex items-center justify-center gap-1">
                  <span>Scope %</span>
                  <SortIcon column="scope_completion" />
                  <Tooltip content={
                    <>
                      <strong>DealHub Calculated:</strong><br/>
                      • 0% - Not started<br/>
                      • 25% - Draft created<br/>
                      • 50% - Hours estimated<br/>
                      • 100% - Sent to customer<br/><br/>
                      Estimated completion of scope configuration.
                    </>
                  }>
                    <Info className="h-3 w-3 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-help" />
                  </Tooltip>
                </div>
              </th>
              {/* 4. Blueprint % */}
              <th
                className="text-center px-3 py-3 text-xs font-semibold text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)]"
                onClick={() => handleSort('blueprint_completion')}
                style={{ width: '100px', minWidth: '100px', maxWidth: '100px' }}
              >
                <div className="flex items-center justify-center gap-1">
                  <span>Blueprint</span>
                  <SortIcon column="blueprint_completion" />
                  <Tooltip content={
                    <>
                      <strong>Blueprint Completion:</strong><br/>
                      9 fields across problem hypothesis, pain points,<br/>
                      vendors, countries, stakeholders & prep checklist.<br/><br/>
                      Blueprint is the first step — capture the architectural<br/>
                      hypothesis before scoping.
                    </>
                  }>
                    <Info className="h-3 w-3 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-help" />
                  </Tooltip>
                </div>
              </th>
              {/* 7c. Close Signal */}
              <ColumnHeader label="Close Signal" sortable sortColumn="close_signal" width="130px" tooltip={
                <>
                  <strong>AI Close Prediction</strong><br/><br/>
                  Daily analysis of HubSpot emails, calls, and meetings to predict deal close likelihood.<br/><br/>
                  Signals:<br/>
                  Hot = closing within 2 weeks<br/>
                  Warm = closing within 1-2 months<br/>
                  Cool = uncertain timeline<br/>
                  Cold = stalled or at risk<br/><br/>
                  Trend arrows show change vs. previous analysis.
                </>
              } />
              {/* 5. Last Activity */}
              <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-secondary)]" style={{ width: '120px', minWidth: '120px', maxWidth: '120px' }}>
                <div className="flex items-center gap-1">
                  <span>Last Activity</span>
                  <Tooltip content={
                    <>
                      <strong>HubSpot Field:</strong><br/>
                      • hs_lastmodifieddate<br/><br/>
                      Last time the deal was modified in HubSpot.
                    </>
                  }>
                    <Info className="h-3 w-3 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-help" />
                  </Tooltip>
                </div>
              </th>
              {/* 7. Stage */}
              <ColumnHeader label="Stage" sortable sortColumn="stage" width="200px" tooltip={
                <>
                  <strong>HubSpot Field:</strong><br/>
                  • dealstage<br/>
                  Current deal stage from the pipeline.<br/>
                  Sort: Closed Won → Closed Lost → Open (by stage number).<br/>
                  Within the same stage, higher Close % floats to the top.
                </>
              } />
              {/* 7b. Close Date & % — HubSpot closedate + hs_deal_stage_probability */}
              <th
                className="text-right px-3 py-3 text-xs font-semibold text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)]"
                onClick={() => handleSort('probability')}
                style={{ width: '110px', minWidth: '110px', maxWidth: '110px' }}
              >
                <div className="flex items-center justify-end gap-1">
                  <span>Close</span>
                  <SortIcon column="probability" />
                  <Tooltip content={
                    <>
                      <strong>HubSpot Fields:</strong><br/>
                      • closedate (close date)<br/>
                      • hs_deal_stage_probability (close %)<br/><br/>
                      Sorted by close date (soonest first) so implementations can see what&apos;s coming up. Close % shown as secondary info.
                    </>
                  }>
                    <Info className="h-3 w-3 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-help" />
                  </Tooltip>
                </div>
              </th>
              {/* 8. Amount */}
              <th
                className="text-right px-4 py-3 text-xs font-semibold text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)]"
                onClick={() => handleSort('amount')}
                style={{ width: '110px', minWidth: '110px', maxWidth: '110px' }}
              >
                <div className="flex items-center justify-end gap-1">
                  <span>Amount</span>
                  <SortIcon column="amount" />
                  <Tooltip content={
                    <>
                      <strong>HubSpot Field:</strong><br/>
                      • amount<br/><br/>
                      Deal value in USD.
                    </>
                  }>
                    <Info className="h-3 w-3 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-help" />
                  </Tooltip>
                </div>
              </th>
              {/* 9. Days Open */}
              <th
                className="text-right px-4 py-3 text-xs font-semibold text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)]"
                onClick={() => handleSort('days_open')}
                style={{ width: '100px', minWidth: '100px', maxWidth: '100px' }}
              >
                <div className="flex items-center justify-end gap-1">
                  <span>Days Open</span>
                  <SortIcon column="days_open" />
                  <Tooltip content={
                    <>
                      <strong>HubSpot Fields:</strong><br/>
                      • presales_engagement_date<br/>
                      • closedate (for closed deals)<br/><br/>
                      Calculated: For open deals (today - engagement date). For closed deals (close date - engagement date).
                    </>
                  }>
                    <Info className="h-3 w-3 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-help" />
                  </Tooltip>
                </div>
              </th>
              {/* 10. Engagement Date */}
              <th
                className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)]"
                onClick={() => handleSort('engagement_date')}
                style={{ width: '130px', minWidth: '130px', maxWidth: '130px' }}
              >
                <div className="flex items-center gap-1">
                  <span>Engagement Date</span>
                  <SortIcon column="engagement_date" />
                  <Tooltip content={
                    <>
                      <strong>HubSpot Field:</strong><br/>
                      • presales_engagement_date<br/><br/>
                      The date presales first engaged. This is the key date for attribution.
                    </>
                  }>
                    <Info className="h-3 w-3 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-help" />
                  </Tooltip>
                </div>
              </th>
              {/* 10b. Handover Date (handover tab only) */}
              {dealsTab === 'handover' && (
                <th
                  className="text-left px-4 py-3 text-xs font-semibold text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)]"
                  onClick={() => handleSort('handover_date')}
                  style={{ width: '140px', minWidth: '140px', maxWidth: '140px' }}
                >
                  <div className="flex items-center gap-1">
                    <span>Handover Date</span>
                    <SortIcon column="handover_date" />
                    <Tooltip content={
                      <>
                        <strong>Handover to Implementations:</strong><br/>
                        • HubSpot field <code>handover_to_implementations_date</code><br/>
                        • Falls back to scope status timeline (<code>handover</code> stage).<br/><br/>
                        Date this deal was handed over to the Melbourne implementations team.
                      </>
                    }>
                      <Info className="h-3 w-3 text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-help" />
                    </Tooltip>
                  </div>
                </th>
              )}
              {/* Tasks */}
              <th className="text-center px-4 py-3 text-xs font-semibold text-[var(--text-secondary)]" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                <span>Tasks</span>
              </th>
              {/* 15. HubSpot Link */}
              <th className="text-center px-4 py-3 text-xs font-semibold text-[var(--text-secondary)]" style={{ width: '80px', minWidth: '80px', maxWidth: '80px' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border-color)]">
            {activeDeals.length === 0 ? (
              <tr>
                <td colSpan={dealsTab === 'handover' ? 14 : 13} className="px-4 py-8 text-center text-[var(--text-muted)]">
                  No deals found matching your filters
                </td>
              </tr>
            ) : (
              activeDeals.map((deal) => (
                <tr
                  key={deal.hubspot_deal_id}
                  className="hover:bg-[var(--bg-secondary)] transition-colors cursor-pointer"
                  onClick={() => navigate(`/dashboard/presales/deal/${deal.hubspot_deal_id}`)}
                >
                  {/* 1. Deal Name */}
                  <td className="px-4 py-3 sticky left-0 z-10 bg-[var(--bg-primary)] shadow-[2px_0_4px_rgba(0,0,0,0.1)]">
                    {deal.dealhub_deal_id ? (
                      <a
                        href={`/scope?session=${deal.dealhub_deal_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary hover:text-primary-light hover:underline flex items-center gap-1 group"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {deal.scope_customer_name || deal.deal_name}
                        <ExternalLink size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    ) : (
                      <div className="font-medium text-[var(--text-primary)]">{deal.scope_customer_name || deal.deal_name}</div>
                    )}
                    {deal.scope_customer_name && deal.scope_customer_name !== deal.deal_name && (
                      <div className="text-[11px] text-[var(--text-muted)] truncate">{deal.deal_name}</div>
                    )}
                    <div className="flex items-center gap-1.5 mt-1">
                      {deal.dealtype && (
                        <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${
                          deal.dealtype === 'newbusiness'
                            ? 'bg-emerald-900/30 text-emerald-300 border border-emerald-700/40'
                            : deal.dealtype === 'Upsell' || deal.dealtype.toLowerCase() === 'upsell'
                            ? 'bg-purple-900/30 text-purple-300 border border-purple-700/40'
                            : deal.dealtype.toLowerCase().includes('strategic')
                            ? 'bg-amber-900/30 text-amber-300 border border-amber-700/40'
                            : 'bg-gray-800/30 text-gray-400 border border-gray-600/40'
                        }`}>
                          {deal.dealtype === 'newbusiness' ? 'New Business'
                            : deal.dealtype === 'Upsell' || deal.dealtype.toLowerCase() === 'upsell' ? 'Upsell'
                            : deal.dealtype.toLowerCase().includes('strategic') ? 'Strategic'
                            : deal.dealtype}
                        </span>
                      )}
                      <span className="text-[11px] text-[var(--text-muted)]">
                        {getFirstName(deal.deal_owner_name)} / {getFirstName(deal.presales_engineer)}
                      </span>
                    </div>
                  </td>
                  {/* 2. Deal Actions — compact progress donut */}
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const { actions: stepActions, scopeStatusDates } = dealToStepSources(deal)
                      const statuses = computeStepStatuses(stepActions, scopeStatusDates, deal.blueprint_completion_percent ?? 0)
                      return (
                        <ProgressDonut
                          statuses={statuses}
                          size={72}
                          centreMode="compact"
                          onSliceClick={(def, status) => {
                            if (!status.toggleable) return
                            // Single-action slice: plain toggle.
                            if (status.singleActionId) {
                              const currentChecked = status.state === 'done'
                              handleToggleAction(deal.id, status.singleActionId, currentChecked)
                              return
                            }
                            // Multi-action slice (e.g. Sandbox/UAT): cycle through alternatives.
                            // cleared → actionIds[0] → actionIds[1] → … → cleared
                            if (def.source.kind !== 'action') return
                            const ids = def.source.actionIds
                            const completedIdx = ids.findIndex((id) => !!stepActions.find((a) => a.id === id && a.completed))
                            if (completedIdx === -1) {
                              // Nothing selected — set the first alternative. (Server mutex handles peers.)
                              handleToggleAction(deal.id, ids[0], false)
                            } else if (completedIdx < ids.length - 1) {
                              // Advance to the next alternative. Server-side mutex unsets the current one.
                              handleToggleAction(deal.id, ids[completedIdx + 1], false)
                            } else {
                              // Last alternative selected — clicking again clears it.
                              handleToggleAction(deal.id, ids[completedIdx], true)
                            }
                          }}
                        />
                      )
                    })()}
                  </td>
                  {/* 3. Hours */}
                  <td className="px-2 py-3 text-sm text-[var(--text-secondary)]">
                    {(() => {
                      const linkData = getEventLinkData(deal.id)
                      const presalesMins = linkData?.presales_minutes || 0
                      const f1Mins = linkData?.frankieone_total_minutes || 0
                      const presalesHrs = (presalesMins / 60).toFixed(1)
                      const f1Hrs = (f1Mins / 60).toFixed(1)

                      if (presalesMins > 0 || f1Mins > 0) {
                        return (
                          <Tooltip content={
                            <div className="space-y-1 text-xs">
                              <div className="font-semibold border-b border-gray-600 pb-1">Time Breakdown</div>
                              <div>Presales: {presalesHrs}h</div>
                              <div>FrankieOne: {f1Hrs}h</div>
                              <div>Demos: {linkData?.demo_count || 0}</div>
                            </div>
                          }>
                            <span className="cursor-help border-b border-dotted border-[var(--text-secondary)] whitespace-nowrap">
                              {presalesHrs}<span className="text-[var(--text-muted)]">/</span>{f1Hrs}h
                            </span>
                          </Tooltip>
                        )
                      }
                      return deal.total_presales_hours > 0 ? `${deal.total_presales_hours}h` : '-'
                    })()}
                  </td>
                  {/* 3. Scope % */}
                  <td className="px-4 py-3 text-sm text-center">
                    {deal.scope_completion_percent !== undefined && (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              deal.scope_completion_percent === 100
                                ? 'bg-green-500'
                                : deal.scope_completion_percent >= 50
                                ? 'bg-blue-500'
                                : 'bg-yellow-500'
                            }`}
                            style={{ width: `${deal.scope_completion_percent}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-[var(--text-secondary)]">
                          {deal.scope_completion_percent}%
                        </span>
                      </div>
                    )}
                  </td>
                  {/* 4. Blueprint % */}
                  <td className="px-3 py-3 text-sm text-center">
                    {(() => {
                      const bp = deal.blueprint_completion_percent ?? 0
                      return (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-12 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                bp === 100
                                  ? 'bg-green-500'
                                  : bp >= 50
                                  ? 'bg-violet-500'
                                  : bp > 0
                                  ? 'bg-violet-400'
                                  : ''
                              }`}
                              style={{ width: `${bp}%` }}
                            />
                          </div>
                          <span className={`text-xs font-medium ${bp > 0 ? 'text-violet-400' : 'text-[var(--text-muted)]'}`}>
                            {bp}%
                          </span>
                        </div>
                      )
                    })()}
                  </td>
                  {/* 7c. Close Signal */}
                  <td className="px-3 py-3 text-sm">
                    {deal.close_signal_strength ? (
                      <Tooltip content={
                        <div className="max-w-xs">
                          <div className="font-semibold mb-1">
                            {deal.close_signal_strength.charAt(0).toUpperCase() + deal.close_signal_strength.slice(1)} — {deal.close_signal_window || 'unknown'}
                          </div>
                          <div className="text-xs mb-2">{deal.close_signal_summary}</div>
                          {deal.close_signal_key_signals && (() => {
                            try {
                              const signals = JSON.parse(deal.close_signal_key_signals)
                              if (signals.length === 0) return null
                              return (
                                <div className="text-xs mb-1">
                                  <span className="font-medium">Signals:</span>
                                  <ul className="list-disc ml-3 mt-0.5">
                                    {signals.map((s: string, i: number) => <li key={i}>{s}</li>)}
                                  </ul>
                                </div>
                              )
                            } catch { return null }
                          })()}
                          {deal.close_signal_risk_flags && (() => {
                            try {
                              const flags = JSON.parse(deal.close_signal_risk_flags)
                              if (flags.length === 0) return null
                              return (
                                <div className="text-xs">
                                  <span className="font-medium">Risks:</span>
                                  <ul className="list-disc ml-3 mt-0.5">
                                    {flags.map((f: string, i: number) => <li key={i}>{f}</li>)}
                                  </ul>
                                </div>
                              )
                            } catch { return null }
                          })()}
                          {deal.close_signal_analyzed_at && (
                            <div className="text-[10px] text-[var(--text-muted)] mt-1 border-t border-[var(--border-color)] pt-1">
                              Analyzed: {formatDate(deal.close_signal_analyzed_at)}
                            </div>
                          )}
                        </div>
                      }>
                        <div className="flex items-center gap-1 cursor-help">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                            deal.close_signal_strength === 'hot'
                              ? 'bg-red-900/40 text-red-300 border border-red-700/50'
                              : deal.close_signal_strength === 'warm'
                              ? 'bg-orange-900/40 text-orange-300 border border-orange-700/50'
                              : deal.close_signal_strength === 'cool'
                              ? 'bg-blue-900/40 text-blue-300 border border-blue-700/50'
                              : 'bg-gray-800/40 text-gray-400 border border-gray-600/50'
                          }`}>
                            {deal.close_signal_strength}
                          </span>
                          {deal.close_signal_trend === 'heating_up' && (
                            <span className="text-green-400 text-xs" title="Heating up">&#9650;</span>
                          )}
                          {deal.close_signal_trend === 'cooling_down' && (
                            <span className="text-red-400 text-xs" title="Cooling down">&#9660;</span>
                          )}
                        </div>
                      </Tooltip>
                    ) : (
                      <span className="text-[var(--text-muted)] text-xs">-</span>
                    )}
                  </td>
                  {/* 5. Last Activity */}
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                    {formatDate(deal.last_activity_date)}
                  </td>
                  {/* 7. Stage */}
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                    <div className="flex items-center gap-2">
                      {getStatusBadge(deal)}
                      <span>{deal.dealstage || '-'}</span>
                    </div>
                  </td>
                  {/* 7b. Close Date & % */}
                  <td className="px-3 py-3 text-sm text-right">
                    <div className="flex flex-col items-end gap-0.5">
                      <span className="text-[var(--text-primary)] text-xs">
                        {deal.close_date ? formatDate(deal.close_date) : '-'}
                      </span>
                      {deal.deal_stage_probability != null ? (
                        (() => {
                          const pct = Math.round(deal.deal_stage_probability * 100)
                          const colour = pct >= 80 ? 'text-green-400' : pct >= 50 ? 'text-blue-300' : pct >= 20 ? 'text-amber-300' : 'text-[var(--text-muted)]'
                          return <span className={`font-mono text-xs ${colour}`}>{pct}%</span>
                        })()
                      ) : null}
                    </div>
                  </td>
                  {/* 8. Amount */}
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)] text-right font-mono">
                    {formatCurrency(deal.amount)}
                  </td>
                  {/* 9. Days Open */}
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)] text-right">
                    {deal.days_open || '-'}
                  </td>
                  {/* 10. Engagement Date */}
                  <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                    {formatDate(deal.presales_engagement_date)}
                  </td>
                  {/* 10b. Handover Date (handover tab only) */}
                  {dealsTab === 'handover' && (
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                      {formatDate(deal.handover_date ?? null)}
                    </td>
                  )}
                  {/* Tasks */}
                  <td className="px-4 py-3 text-sm text-center">
                    {(() => {
                      const open = deal.task_count_open ?? 0
                      const total = deal.task_count_total ?? 0
                      if (total === 0) return <span className="text-[var(--text-muted)]">-</span>
                      return (
                        <span className={`font-medium ${open > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                          {open}/{total}
                        </span>
                      )
                    })()}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <a
                      href={`https://app.hubspot.com/contacts/YOUR_PORTAL_ID/deal/${deal.hubspot_deal_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[var(--button-primary-bg)] hover:text-[var(--button-primary-hover)] transition-colors"
                      title="View in HubSpot"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Early Deals / Prospect Section */}
      {eventLinkSummary && eventLinkSummary.by_prospect && eventLinkSummary.by_prospect.length > 0 && (
        <EarlyDealsSection />
      )}
    </Card>
  )
}

function EarlyDealsSection() {
  const { eventLinkSummary, reassignProspectEvents } = useCalendarStore()
  const [expanded, setExpanded] = useState(false)
  const [reassigningCompany, setReassigningCompany] = useState<string | null>(null)

  const prospects = eventLinkSummary?.by_prospect || []
  if (prospects.length === 0) return null

  const totalMinutes = prospects.reduce((sum, p) => sum + p.presales_minutes, 0)
  const totalEvents = prospects.reduce((sum, p) => sum + p.event_count, 0)

  const handleReassign = async (_prospectCompany: string, dealId: number) => {
    await reassignProspectEvents(_prospectCompany, dealId)
    setReassigningCompany(null)
    // Summary refreshed by the store action
  }

  return (
    <div className="border-t border-[var(--border-color)]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-[var(--bg-secondary)] transition-colors text-left"
      >
        <Building2 className="w-4 h-4 text-amber-400" />
        <span className="text-sm font-semibold text-amber-400">
          Early Deals (Unattributed)
        </span>
        <span className="text-xs text-[var(--text-muted)]">
          {totalEvents} event{totalEvents !== 1 ? 's' : ''} · {(totalMinutes / 60).toFixed(1)}h
        </span>
        <ChevronDown className={`w-4 h-4 text-[var(--text-muted)] ml-auto transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--border-color)]">
                <th className="text-left py-2 px-2 font-semibold text-[var(--text-secondary)]" style={{ minWidth: '160px' }}>Company</th>
                <th className="text-right py-2 px-2 font-semibold text-[var(--text-secondary)]">Events</th>
                <th className="text-right py-2 px-2 font-semibold text-[var(--text-secondary)]">Hours</th>
                <th className="text-right py-2 px-2 font-semibold text-[var(--text-secondary)]">Demos</th>
                <th className="text-left py-2 px-2 font-semibold text-[var(--text-secondary)]" style={{ minWidth: '200px' }}></th>
              </tr>
            </thead>
            <tbody>
              {prospects.map((prospect) => (
                <tr
                  key={prospect.prospect_company}
                  className="border-b border-[var(--border-color)] hover:bg-[var(--bg-secondary)]"
                >
                  <td className="py-2 px-2 text-amber-400/80">
                    <div className="flex items-center gap-1.5">
                      <span>🏢</span>
                      <span className="font-medium">{prospect.prospect_company}</span>
                      <span className="text-[9px] px-1 py-0.5 rounded bg-amber-900/30 border border-amber-600/30 text-amber-400 font-semibold uppercase tracking-wider">Early</span>
                    </div>
                  </td>
                  <td className="text-right py-2 px-2 text-[var(--text-secondary)]">{prospect.event_count}</td>
                  <td className="text-right py-2 px-2 text-[var(--text-secondary)]">{(prospect.presales_minutes / 60).toFixed(1)}h</td>
                  <td className="text-right py-2 px-2 text-[var(--text-secondary)]">{prospect.demo_count || 0}</td>
                  <td className="py-2 px-2">
                    {reassigningCompany === prospect.prospect_company ? (
                      <ReassignProspectDialog
                        prospectCompany={prospect.prospect_company}
                        onReassign={(dealId) => handleReassign(prospect.prospect_company, dealId)}
                        onCancel={() => setReassigningCompany(null)}
                      />
                    ) : (
                      <button
                        onClick={() => setReassigningCompany(prospect.prospect_company)}
                        className="text-xs text-[var(--button-primary-bg)] hover:text-[var(--button-primary-hover)] transition-colors"
                      >
                        Reassign to Deal →
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
